import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { NewAdvertiserModal } from "./NewAdvertiserModal";
import { CampaignSettingsApprovalModal } from "./CampaignSettingsApprovalModal";
import { AdvertiserSettingsModal } from "./AdvertiserSettingsModal";
import { AnnouncementManagementModal } from "./AnnouncementManagementModal";
import { PendingChip } from "../../components/PendingChip";
import { PageBackground } from "../../components/PageBackground";
import { Toast, useToast } from "../../components/Toast";

export function SupportAdvertiserList() {
  const { user, signOut, setMasquerade, isSupportUser } = useAuth();
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showNewAdvertiserModal, setShowNewAdvertiserModal] = useState(false);
  const [advertisersRefresh, setAdvertisersRefresh] = useState(0);
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingItemsLoading, setPendingItemsLoading] = useState(true);
  const [reviewingItem, setReviewingItem] = useState(null);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [reviewModal, setReviewModal] = useState(null); // { advertiserId, advertiserName }
  const [pendingRefresh, setPendingRefresh] = useState(0);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [toast, showToast] = useToast();

  useEffect(() => {
    if (!supabase) { setPendingItemsLoading(false); return; }
    supabase
      .from("campaign_settings")
      .select("id, advertiser_id, month_year, created_at, advertisers(name)")
      .eq("status", "pending")
      .order("created_at", { ascending: true })
      .limit(50)
      .then(({ data }) => {
        setPendingItems(data ?? []);
        setPendingItemsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!search.trim()) { setDebouncedSearch(""); return; }
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 280);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!supabase) { setError("Supabase not configured"); return; }
    if (!debouncedSearch) { setAdvertisers([]); setError(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const q = debouncedSearch;
    const num = parseInt(q, 10);
    const isNumeric = !isNaN(num) && String(num) === q.trim();
    (async () => {
      let query = supabase.from("advertisers").select("id, name, created_at").limit(25).order("name", { ascending: true });
      if (isNumeric) query = query.or(`name.ilike.%${q}%,id.eq.${num}`);
      else query = query.ilike("name", `%${q}%`);
      const { data, error } = await query;
      if (cancelled) return;
      setLoading(false);
      if (error) { setError(error.message); setAdvertisers([]); }
      else setAdvertisers(data ?? []);
    })();
    return () => { cancelled = true; };
  }, [debouncedSearch, advertisersRefresh]);

  useEffect(() => {
    if (!supabase) return;
    setPendingLoading(true);
    supabase
      .from("advertiser_pending_settings")
      .select("id, advertiser_id, name, website, category, advertisers(name)")
      .order("created_at", { ascending: true })
      .then(({ data, error }) => {
        if (!error) setPendingReviews(data ?? []);
      })
      .finally(() => setPendingLoading(false));
  }, [pendingRefresh]);

  const handleReviewSaved = useCallback((warning) => {
    setReviewModal(null);
    setPendingRefresh(r => r + 1);
    if (warning) showToast(warning, "warning");
  }, [showToast]);

  const handleViewAs = (advertiser) => {
    setMasquerade(advertiser.id, advertiser.name);
    window.location.assign("/");
  };

  const showHint = !search.trim();
  const showEmpty = search.trim() && !loading && advertisers.length === 0 && !error;

  return (
    <div style={{ minHeight:"100vh", background:"#FFFFFF", fontFamily:"'DM Sans','Helvetica Neue',sans-serif", color:"#151515", display:"flex", flexDirection:"column", position:"relative", overflow:"hidden" }}>
      <PageBackground />
      <div style={{ background:"rgba(255,255,255,0.85)", backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)", borderBottom:"1px solid #dad8d9", padding:"16px 24px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, position:"relative", zIndex:1 }}>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <span style={{ fontSize:10, color:"#F90091", letterSpacing:1.5, textTransform:"uppercase", fontWeight:600 }}>Internal support</span>
          <span style={{ color:"#626262", fontSize:13 }}>{user?.email}</span>
        </div>
        <a href="/" style={{ fontSize:13, color:"#626262", textDecoration:"none" }}>← Main app</a>
        <button onClick={() => signOut()} style={{ padding:"8px 14px", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:12, cursor:"pointer" }}>Sign out</button>
      </div>
      <div style={{ flex:1, padding:28, maxWidth:900, margin:"0 auto", width:"100%", position:"relative", zIndex:1 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:24 }}>
          <div>
            <h1 style={{ fontSize:28, fontWeight:600, color:"#151515", marginBottom:8, fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>Advertisers</h1>
            <p style={{ color:"#626262", fontSize:14, margin:0 }}>Type an advertiser name or ID to search, then select one to view as.</p>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            {isSupportUser && (
              <button
                onClick={() => setShowAnnouncements(true)}
                style={{ padding:"10px 18px", background:"transparent", color:"#F90091", border:"1px solid #F90091", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer", flexShrink:0 }}
              >
                Manage Announcements
              </button>
            )}
            <button
              onClick={() => setShowNewAdvertiserModal(true)}
              style={{ padding:"10px 18px", background:"#F90091", color:"#fff", border:"none", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer", flexShrink:0 }}
            >
              Create New Advertiser
            </button>
          </div>
        </div>
        {!pendingItemsLoading && pendingItems.length > 0 && (
          <div style={{ marginBottom:28 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
              <span style={{ fontSize:15, fontWeight:700, color:"#151515" }}>Pending Campaign Settings Review</span>
              <span style={{ fontSize:11, fontWeight:600, background:"#FEF3C7", color:"#92400E", padding:"2px 8px", borderRadius:20 }}>
                {pendingItems.length} pending
              </span>
            </div>
            <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:14, overflow:"hidden", boxShadow:"0 3px 15px rgba(0,0,0,0.08)" }}>
              {pendingItems.map((item, i) => {
                const name = item.advertisers?.name ?? `Advertiser #${item.advertiser_id}`;
                const month = new Date(item.month_year + "T00:00:00Z")
                  .toLocaleString("default", { month:"long", year:"numeric", timeZone:"UTC" });
                const diffMs = Date.now() - new Date(item.created_at).getTime();
                const days = Math.floor(diffMs / 86400000);
                const ago = days === 0 ? "today" : days === 1 ? "1 day ago" : `${days} days ago`;
                return (
                  <div key={item.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 20px", borderBottom: i < pendingItems.length - 1 ? "1px solid #F0F0F0" : "none" }}>
                    <button
                      onClick={() => { setMasquerade(item.advertiser_id, name); window.location.href = "/"; }}
                      style={{ flex:"1 1 160px", fontSize:13, fontWeight:600, color:"#F90091", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", background:"none", border:"none", cursor:"pointer", padding:0, textAlign:"left" }}
                    >
                      {name} <span style={{ fontWeight:400, color:"#7b7b7b" }}>({item.advertiser_id})</span>
                    </button>
                    <span style={{ flex:"0 0 auto", fontSize:12, color:"#626262" }}>{month}</span>
                    <span style={{ flex:"0 0 auto", fontSize:11, color:"#7b7b7b" }}>{ago}</span>
                    <button
                      onClick={() => setReviewingItem({ advertiser_id: item.advertiser_id, name, month_year: item.month_year })}
                      style={{ padding:"6px 14px", background:"#FFE9F6", color:"#F90091", border:"1px solid #F9009133", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap" }}
                    >
                      Review →
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Pending advertiser profile change reviews */}
        {!pendingLoading && pendingReviews.length > 0 && (
          <div style={{ marginBottom:28 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
              <span style={{ fontSize:13, fontWeight:600, color:"#151515" }}>Pending Advertiser Settings Reviews</span>
              <PendingChip label={`${pendingReviews.length} pending`} />
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:0, background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:14, overflow:"hidden", boxShadow:"0 3px 15px rgba(0,0,0,0.08)" }}>
              {pendingReviews.map(row => {
                const changed = ["name","website","category"].filter(k => row[k] != null);
                return (
                  <div key={row.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", borderBottom:"1px solid #F0F0F0", gap:16 }}>
                    <div>
                      <div style={{ fontWeight:600, color:"#151515", fontSize:14 }}>{row.advertisers?.name ?? `Advertiser #${row.advertiser_id}`}</div>
                      <div style={{ fontSize:12, color:"#7b7b7b", marginTop:3 }}>
                        Wants to change: {changed.join(", ") || "—"}
                      </div>
                    </div>
                    <button
                      onClick={() => setReviewModal({ advertiserId: row.advertiser_id, advertiserName: row.advertisers?.name ?? `Advertiser #${row.advertiser_id}` })}
                      style={{ padding:"8px 16px", background:"#F90091", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer", flexShrink:0 }}
                    >
                      Review
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div style={{ marginBottom:20 }}>
          <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:10, padding:"10px 14px", display:"flex", alignItems:"center", gap:8, boxShadow:"0 2px 6px rgba(0,0,0,0.06)" }}>
            <span style={{ color:"#7b7b7b", fontSize:14 }}>🔍</span>
            <input
              placeholder="Type name or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{ flex:1, background:"transparent", border:"none", outline:"none", color:"#151515", fontSize:14 }}
            />
            {loading && <span style={{ fontSize:12, color:"#7b7b7b" }}>Searching…</span>}
          </div>
        </div>
        {error && (
          <div style={{ padding:12, marginBottom:16, background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:10, color:"#e53935", fontSize:13 }}>{error}</div>
        )}
        <div style={{ display:"flex", flexDirection:"column", gap:0, background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:14, overflow:"hidden", boxShadow:"0 3px 15px rgba(0,0,0,0.08)" }}>
          {showHint && (
            <div style={{ padding:32, textAlign:"center", color:"#7b7b7b", fontSize:13 }}>Type an advertiser name or ID above to see results.</div>
          )}
          {showEmpty && (
            <div style={{ padding:32, textAlign:"center", color:"#7b7b7b", fontSize:13 }}>No advertisers match &quot;{debouncedSearch}&quot;.</div>
          )}
          {!showHint && !showEmpty && advertisers.map((a) => (
            <div
              key={a.id}
              style={{
                display:"flex",
                alignItems:"center",
                justifyContent:"space-between",
                padding:"14px 20px",
                borderBottom:"1px solid #F0F0F0",
                gap:16,
              }}
            >
              <div>
                <div style={{ fontWeight:600, color:"#151515", fontSize:14 }}>{a.name || "—"}</div>
                <div style={{ fontSize:12, color:"#7b7b7b", marginTop:2 }}>ID: {a.id}</div>
              </div>
              <button
                onClick={() => handleViewAs(a)}
                style={{ padding:"8px 16px", background:"#F90091", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer", flexShrink:0 }}
              >
                View as advertiser
              </button>
            </div>
          ))}
        </div>
      </div>
      {showAnnouncements && (
        <AnnouncementManagementModal onClose={() => setShowAnnouncements(false)} />
      )}
      {showNewAdvertiserModal && (
        <NewAdvertiserModal
          onClose={() => setShowNewAdvertiserModal(false)}
          onCreated={() => setAdvertisersRefresh((r) => r + 1)}
        />
      )}
      {reviewingItem && (
        <CampaignSettingsApprovalModal
          advertiserId={reviewingItem.advertiser_id}
          advertiserName={reviewingItem.name}
          onClose={() => setReviewingItem(null)}
          onActioned={(resumeMessage) => {
            setPendingItems((prev) => prev.filter((i) => i.advertiser_id !== reviewingItem.advertiser_id));
            setReviewingItem(null);
            if (resumeMessage) showToast(resumeMessage.message, resumeMessage.type);
          }}
        />
      )}
      {reviewModal && (
        <AdvertiserSettingsModal
          advertiserId={reviewModal.advertiserId}
          advertiserName={reviewModal.advertiserName}
          onClose={() => setReviewModal(null)}
          onSaved={handleReviewSaved}
        />
      )}

      <Toast {...toast} />
    </div>
  );
}
